import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Consola from './Consola';
import Tutorial from './Tutorial';
import Sqlcontent from './Sqlcontent';
import RemoteContent from './RemoteContent';
import { generarUUID } from './generadorUUID';
import LoginModal from './Login';
import Onboarding from './Onboarding';
import config from '../config.json';
import UserMenu from './UserMenu';
import WhatsAppButton from './Whatsappbutton';

interface RemoteCredentials {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
}

interface RemoteState {
  credentials: RemoteCredentials;
  connected: boolean;
  remoteTables: string[];
  selectedRemote: Set<string>;
  newTables: string[];
  selectedNew: Set<string>;
}

const defaultRemoteState: RemoteState = {
  credentials: { host: '', port: '', database: '', username: '', password: '' },
  connected: false,
  remoteTables: [],
  selectedRemote: new Set(),
  newTables: [],
  selectedNew: new Set(),
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeSection, setActiveSection] =
    useState<'dashboard' | 'terminal' | 'sql' | 'remote'>('dashboard');
  const [sessionUUID, setSessionUUID] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('onboardingDone')
  );
  const [remoteState, setRemoteState] = useState<RemoteState>(defaultRemoteState);

  // ── Cuando user cambia (login/logout), releer el UUID del localStorage ──
  useEffect(() => {
    let storedUUID = localStorage.getItem('sessionUUID');
    if (!storedUUID) {
      storedUUID = generarUUID();
      localStorage.setItem('sessionUUID', storedUUID);
    }
    setSessionUUID(storedUUID);
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', sidebarOpen);
  }, [sidebarOpen]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  const handleNavClick = (
    e: React.MouseEvent,
    section: 'dashboard' | 'terminal' | 'sql' | 'remote'
  ) => {
    e.preventDefault();
    if (section === 'dashboard') {
      setActiveSection('dashboard');
      setShowOnboarding(true);
      if (window.innerWidth > 768) closeSidebar();
    } else {
      setShowOnboarding(false);
      setActiveSection(section);
      closeSidebar();
    }
  };

  const handleOnboardingClose = () => {
    localStorage.setItem('onboardingDone', 'true');
    setShowOnboarding(false);
    setSidebarOpen(false);
  };

  const handleDescargarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeSidebar();
    if (user) {
      navigate('/preview');
    } else {
      const modalElement = document.getElementById('loginModal');
      if (modalElement) {
        const modal = new (window as any).bootstrap.Modal(modalElement);
        modal.show();
      }
    }
  };

  const handlePrivacidadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(config.PRIVACY_URL, '_blank');
  };

  return (
    <div className="wrapper">
      {!sidebarOpen && (
        <button className="mobile-menu-toggle" onClick={toggleSidebar}>
          <i className="fas fa-bars" />
        </button>
      )}

      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      />

      <aside className={`main-sidebar sidebar-dark-primary elevation-4 ${sidebarOpen ? 'active' : ''}`}>
        <button className="sidebar-close" onClick={closeSidebar}>
          <i className="fas fa-times" />
        </button>

        <a href="/" className="brand-link">
          <span className="brand-text font-weight-light">OmniCrud</span>
        </a>

        <div className="sidebar">
          <nav className="mt-2">
            <ul className="nav nav-pills nav-sidebar flex-column">

              <li className="nav-item">
                <a
                  href="#"
                  className={`nav-link ${activeSection === 'dashboard' ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, 'dashboard')}
                >
                  <i className="nav-icon fas fa-chalkboard-teacher" />
                  <p>Tutorial</p>
                </a>
              </li>

              <li className="nav-item">
                <a
                  href="#"
                  className={`nav-link ${activeSection === 'terminal' ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, 'terminal')}
                >
                  <i className="nav-icon fas fa-terminal" />
                  <p>Terminal</p>
                </a>
              </li>

              <li className="nav-item">
                <a
                  href="#"
                  className={`nav-link ${activeSection === 'sql' ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, 'sql')}
                >
                  <i className="nav-icon fas fa-magic" />
                  <p>Form Wizard</p>
                </a>
              </li>

              <li className="nav-item">
                <a
                  href="#"
                  className={`nav-link ${activeSection === 'remote' ? 'active' : ''}`}
                  onClick={(e) => handleNavClick(e, 'remote')}
                >
                  <i className="nav-icon fas fa-server" />
                  <p>Remote Connection</p>
                </a>
              </li>

           <li className="nav-item">
       <a href="#" className="nav-link" onClick={handleDescargarClick}>
        <i className={`nav-icon fas ${user ? 'fa-download' : 'fa-sign-in-alt'}`} />
       <p>{user ? 'Descargar Proyecto' : 'Iniciar Sesión'}</p>
       </a>
        </li>

              <li className="nav-item">
                <a href="#" className="nav-link" onClick={handlePrivacidadClick}>
                  <i className="nav-icon fas fa-shield-alt" />
                  <p>Política de Privacidad</p>
                </a>
              </li>

            </ul>
          </nav>
        </div>
      </aside>

      <div className="content-wrapper" style={{ overflow: 'visible' }}>
        <section className="content p-3">
          {activeSection === 'dashboard' && <Tutorial />}
          {activeSection === 'terminal' && <Consola />}
          {activeSection === 'sql' && <Sqlcontent sessionUUID={sessionUUID} />}
          <div style={{ display: activeSection === 'remote' ? 'block' : 'none' }}>
            <RemoteContent
              remoteState={remoteState}
              setRemoteState={setRemoteState}
            />
          </div>
        </section>
      </div>

      {showOnboarding && activeSection === 'dashboard' && (
        <Onboarding
          key={showOnboarding ? 'active' : 'inactive'}
          onClose={handleOnboardingClose}
          onOpenSidebar={() => setSidebarOpen(true)}
        />
      )}

      <UserMenu />
      <LoginModal />
      <WhatsAppButton />
    </div>
  );
};

export default Dashboard;